import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Plus, Pencil, Loader2, CreditCard, Clock } from 'lucide-react';
import { toast } from 'sonner';

const NAVY = 'hsl(var(--c4p-navy-900))';

function OptionSection({ title, icon: Icon, category, options, loading, onRefresh }) {
  const [showAdd, setShowAdd]   = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLabel, setFormLabel]   = useState('');
  const [formOrder, setFormOrder]   = useState(99);
  const [saving, setSaving] = useState(false);

  const openAdd = () => { setFormLabel(''); setFormOrder(options.length + 1); setShowAdd(true); };
  const openEdit = (opt) => { setEditItem(opt); setFormLabel(opt.label); setFormOrder(opt.sort_order); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formLabel.trim()) return toast.error('Label is required');
    setSaving(true);
    try {
      if (editItem) {
        await adminAPI.updateDropdownOption(editItem.id, { label: formLabel, sort_order: formOrder });
        toast.success('Option updated');
        setEditItem(null);
      } else {
        await adminAPI.createDropdownOption({ category, label: formLabel, sort_order: formOrder });
        toast.success(`"${formLabel}" added`);
        setShowAdd(false);
      }
      onRefresh();
    } catch (ex) { toast.error(ex.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (opt) => {
    try {
      await adminAPI.updateDropdownOption(opt.id, { is_active: !opt.is_active });
      toast.success(`"${opt.label}" ${!opt.is_active ? 'activated' : 'deactivated'}`);
      onRefresh();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-4" data-testid={`section-${category}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={16} style={{ color: 'hsl(var(--c4p-brand-blue))' }} />
          <h3 className="text-base font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>{title}</h3>
          <Badge variant="outline" className="text-xs">{options.filter(o => o.is_active).length} active</Badge>
        </div>
        <Button size="sm" onClick={openAdd}
          style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }} className="gap-1.5">
          <Plus size={13} /> Add Option
        </Button>
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ background: 'hsl(var(--muted))' }}>
              <TableHead>Label</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Active</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}><TableCell colSpan={4}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
            )) : options.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No options yet</TableCell></TableRow>
            ) : options.map(opt => (
              <TableRow key={opt.id} style={{ opacity: opt.is_active ? 1 : 0.5 }}>
                <TableCell className="font-medium">{opt.label}</TableCell>
                <TableCell className="text-sm">{opt.sort_order}</TableCell>
                <TableCell>
                  <Switch checked={opt.is_active} onCheckedChange={() => toggleActive(opt)} />
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(opt)} className="gap-1 text-xs">
                    <Pencil size={12} /> Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add {title} Option</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Label *</Label>
              <Input value={formLabel} onChange={e => setFormLabel(e.target.value)} placeholder="e.g. Bank Transfer" required autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label>Sort Order</Label>
              <Input type="number" min={1} value={formOrder} onChange={e => setFormOrder(parseInt(e.target.value) || 99)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} style={{ background: NAVY, color: 'white' }}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit Option</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Label *</Label>
              <Input value={formLabel} onChange={e => setFormLabel(e.target.value)} required autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label>Sort Order</Label>
              <Input type="number" min={1} value={formOrder} onChange={e => setFormOrder(parseInt(e.target.value) || 99)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
              <Button type="submit" disabled={saving} style={{ background: NAVY, color: 'white' }}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function QuoteOptionsAdmin() {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await adminAPI.getAllDropdownOptions();
      setOptions(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load options'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const paymentMethods    = options.filter(o => o.category === 'payment_method');
  const billingFrequencies = options.filter(o => o.category === 'billing_frequency');

  return (
    <div className="space-y-8" data-testid="quote-options-admin">
      <div>
        <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>Quote Options</h2>
        <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
          Manage the dropdown values available on quotes. Changes apply to all new quotes immediately.
        </p>
      </div>

      <OptionSection
        title="Payment Methods"
        icon={CreditCard}
        category="payment_method"
        options={paymentMethods}
        loading={loading}
        onRefresh={load}
      />

      <OptionSection
        title="Billing Frequencies"
        icon={Clock}
        category="billing_frequency"
        options={billingFrequencies}
        loading={loading}
        onRefresh={load}
      />
    </div>
  );
}
